import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, UserPlus, UserMinus, Link as LinkIcon, Search, Mail, Loader2, Copy, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useAuth, API } from '../App';

const CoHostManagementModal = ({ isOpen, onClose, eventId, isHost }) => {
  const { token, user } = useAuth();
  const [coHosts, setCoHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState('email'); // 'email', 'search', 'invite'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [invitationLink, setInvitationLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && eventId) {
      fetchCoHosts();
    }
  }, [isOpen, eventId]);

  const fetchCoHosts = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/co-hosts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoHosts(response.data.co_hosts || []);
    } catch (error) {
      console.error('Failed to fetch co-hosts:', error);
    }
  };

  const handleAddCoHost = async () => {
    if (method !== 'invite' && !email) {
      toast.error('Please enter an email address');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        method,
        email: method !== 'invite' ? email : undefined,
        name: method === 'email' ? name : undefined
      };

      const response = await axios.post(
        `${API}/events/${eventId}/co-hosts`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (method === 'invite') {
        setInvitationLink(response.data.invitation_link);
        toast.success('Invitation link generated!');
      } else {
        toast.success('Co-host added successfully!');
        setEmail('');
        setName('');
        fetchCoHosts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add co-host');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCoHost = async (coHostEmail) => {
    const isSelfRemoval = coHostEmail === user?.email;
    const confirmMessage = isSelfRemoval 
      ? 'Are you sure you want to remove yourself as a co-host? You will lose access to co-host features.'
      : 'Are you sure you want to remove this co-host?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/events/${eventId}/co-hosts/${encodeURIComponent(coHostEmail)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Co-host removed successfully');
      
      if (isSelfRemoval) {
        // If removing self, close modal and refresh dashboard
        onClose();
        // Refresh the page to update permissions
        setTimeout(() => window.location.reload(), 500);
      } else {
        // If host removing someone else, refresh the co-hosts list
        await fetchCoHosts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove co-host');
      console.error('Remove co-host error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyInvitationLink = () => {
    navigator.clipboard.writeText(invitationLink);
    setCopied(true);
    toast.success('Invitation link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center text-indigo-900">
            <Users className="mr-2 h-6 w-6" />
            {isHost ? 'Manage Co-hosts' : 'Co-hosts List'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Current Co-hosts */}
          <div>
            <h3 className="text-lg font-semibold text-indigo-900 mb-3">
              Current Co-hosts ({coHosts.length}/5)
            </h3>
            {coHosts.length === 0 ? (
              <p className="text-gray-600 text-sm">No co-hosts added yet</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {coHosts.map((coHost) => {
                  const isSelf = coHost.email === user?.email;
                  const canRemove = isHost || isSelf;
                  
                  return (
                    <div
                      key={coHost.email}
                      className="flex justify-between items-center p-4 bg-indigo-50 rounded-lg border-2 border-indigo-300 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex-1">
                        <p className="font-bold text-indigo-900 text-lg">
                          {coHost.name}
                          {isSelf && <span className="text-xs ml-2 text-green-600">(You)</span>}
                        </p>
                        <p className="text-sm text-gray-700 mt-1">📧 {coHost.email}</p>
                        <div className="flex gap-3 mt-2">
                          <p className="text-xs text-gray-600">
                            Status: <span className={`font-semibold ${coHost.status === 'active' ? 'text-green-600' : 'text-yellow-600'}`}>
                              {coHost.status === 'active' ? '✓ Active' : '⏳ Invited'}
                            </span>
                          </p>
                          {coHost.added_at && (
                            <p className="text-xs text-gray-600">
                              Added: <span className="font-medium">{new Date(coHost.added_at).toLocaleDateString()}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      {canRemove && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCoHost(coHost.email)}
                          disabled={loading}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-50"
                          title={isSelf ? 'Remove yourself' : 'Remove co-host'}
                        >
                          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Co-host Section - Only for Hosts */}
          {isHost && coHosts.length < 5 && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-indigo-900 mb-3">Add Co-host</h3>
              
              {/* Method Selection */}
              <div className="flex gap-2 mb-4">
                <Button
                  variant={method === 'email' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setMethod('email');
                    setInvitationLink('');
                  }}
                  className={method === 'email' ? 'bg-indigo-600' : ''}
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </Button>
                <Button
                  variant={method === 'search' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setMethod('search');
                    setInvitationLink('');
                  }}
                  className={method === 'search' ? 'bg-indigo-600' : ''}
                >
                  <Search className="h-4 w-4 mr-1" />
                  Search
                </Button>
                <Button
                  variant={method === 'invite' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setMethod('invite');
                    setEmail('');
                    setName('');
                  }}
                  className={method === 'invite' ? 'bg-indigo-600' : ''}
                >
                  <LinkIcon className="h-4 w-4 mr-1" />
                  Invite Link
                </Button>
              </div>

              {/* Input Fields */}
              {method !== 'invite' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="cohost-email" className="text-gray-700 font-semibold">Email Address *</Label>
                    <Input
                      id="cohost-email"
                      type="email"
                      placeholder="Enter email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  {method === 'email' && (
                    <div>
                      <Label htmlFor="cohost-name" className="text-gray-700 font-semibold">Name (Optional)</Label>
                      <Input
                        id="cohost-name"
                        placeholder="Enter name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                  )}
                  <Button
                    onClick={handleAddCoHost}
                    disabled={loading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add Co-host
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Invitation Link */}
              {method === 'invite' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Generate a shareable invitation link. Link expires in 7 days.
                  </p>
                  {!invitationLink ? (
                    <Button
                      onClick={handleAddCoHost}
                      disabled={loading}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <LinkIcon className="mr-2 h-4 w-4" />
                          Generate Invitation Link
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={invitationLink}
                          readOnly
                          className="font-mono text-sm"
                        />
                        <Button
                          onClick={copyInvitationLink}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-green-600 font-medium">
                        ✓ Link generated! Share it with potential co-hosts.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Method Descriptions */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900 font-semibold mb-1">
                  {method === 'email' && '📧 Email Method'}
                  {method === 'search' && '🔍 Search Method'}
                  {method === 'invite' && '🔗 Invite Link Method'}
                </p>
                <p className="text-xs text-blue-800">
                  {method === 'email' && 'Add co-host by email. If they have a Contribo account, they\'ll be added immediately. Otherwise, they\'ll receive an invitation.'}
                  {method === 'search' && 'Search for existing Contribo users by email and add them as co-hosts instantly.'}
                  {method === 'invite' && 'Generate a shareable link. Perfect for sharing via WhatsApp, email, or social media.'}
                </p>
              </div>
            </div>
          )}

          {/* Co-host Permissions Info */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Co-host Permissions:</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>✅ Approve/reject contributions</li>
              <li>✅ Share event</li>
              <li>✅ Download tracker</li>
              <li>✅ Copy event details</li>
              <li>✅ Upgrade plan</li>
              <li>⚠️ Edit event (requires admin approval)</li>
              <li>⚠️ Delete event (requires admin approval)</li>
            </ul>
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CoHostManagementModal;
